/**
 * Normalize a cell value for CSV export.
 *
 * - Strips thousands-separator commas (e.g. "1,234" → "1234")
 * - Converts dash-separated dates (d-m-yyyy or dd-mm-yyyy) → dd/mm/yyyy
 * - Prefixes any date-like value with a leading space so that Excel,
 *   LibreOffice, and Google Sheets all treat it as plain text and never
 *   auto-reformat it based on regional locale settings.
 *   (Matches the Bridge_API_Reference_Procedure export approach.)
 *
 * @param {*} raw - Raw cell value (string, number, null, undefined)
 * @returns {string}
 */
export function normalizeCell(raw) {
    const str = String(raw ?? '').replace(/,/g, ''); // strip thousands separators
    // dd-mm-yyyy or d-m-yyyy → dd/mm/yyyy (replace dashes with slashes)
    const normalized = str.replace(/^(\d{1,2})-(\d{1,2})-(\d{4})$/, '$1/$2/$3');
    // Prefix date-like strings with a space to force all spreadsheet apps
    // (Excel, LibreOffice, Google Sheets) to treat the cell as plain text,
    // preventing locale-based auto-conversion of the date format.
    if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(normalized)) {
        return ' ' + normalized;
    }
    return normalized;
}

/**
 * Export data to CSV file
 * 
 * @param {Array} data - Array of objects to export
 * @param {string} filename - Name of the CSV file (without extension)
 * @param {Array} columns - Column definitions [{key, label}]
 */
export function exportToCSV(data, filename, columns) {
    if (!data || data.length === 0) {
        console.warn('No data to export');
        return;
    }

    try {
        // Generate CSV header
        const headers = columns.map(col => col.label || col.key);
        
        // Generate CSV rows
        const rows = data.map(row => {
            return columns.map(col => {
                const value = row[col.key];
                
                // Handle different data types
                if (value === null || value === undefined) {
                    return '';
                }
                
                // Normalize string values — dates get wrapped in ="..." for Excel safety
                if (typeof value === 'string') {
                    return normalizeCell(value);
                }
                
                // Handle numbers — pass through as-is (no date risk)
                if (typeof value === 'number') {
                    return value;
                }
                
                return normalizeCell(String(value));
            }).join(',');
        });

        // Combine header and rows
        const csvContent = [headers.join(','), ...rows].join('\n');

        // Create blob and download — BOM ensures Excel opens UTF-8 correctly
        const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `${filename}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    } catch (error) {
        console.error('Error exporting to CSV:', error);
    }
}

/**
 * Export chart data to CSV (simplified for chart datasets)
 * 
 * @param {Object} chartData - Chart.js data object with labels and datasets
 * @param {string} filename - Name of the CSV file
 * @param {Object} [options] - Optional overrides: { xLabel, datasetLabels }
 */
export function exportChartDataToCSV(chartData, filename, options = {}) {
    if (!chartData || !chartData.labels) {
        console.warn('No chart data to export');
        return;
    }

    try {
        const { labels, datasets } = chartData;

        // Allow callers to override the x-axis column name and dataset column names
        const xLabel = options.xLabel || 'Label';
        const datasetLabels = options.datasetLabels || datasets.map(ds => ds.label || 'Value');

        // Build header row
        const headers = [xLabel, ...datasetLabels];
        
        // Build data rows — normalizeCell applied to labels (often dates) and values
        const rows = labels.map((label, index) => {
            const row = [normalizeCell(label)];
            datasets.forEach(dataset => {
                const value = dataset.data[index];
                row.push(value !== null && value !== undefined ? normalizeCell(value) : '');
            });
            return row.join(',');
        });

        // Combine and download — BOM ensures Excel opens UTF-8 correctly
        const csvContent = [headers.join(','), ...rows].join('\n');
        const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `${filename}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    } catch (error) {
        console.error('Error exporting chart data to CSV:', error);
    }
}

export default exportToCSV;
